import React, { useMemo, useState } from "react";
import { Radio, Skeleton, Tag } from "antd";
import { ClockCircleOutlined, UserOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import useUser from "../../hooks/useUser";
import { AdminPageHeader } from "../../components/admin-shell/AdminShell";

const CATEGORY_COLORS = {
  "Feature Request": "blue",
  "Bug Report": "red",
  "UI/UX": "purple",
  "Blood Donation": "volcano",
  Community: "green",
  Other: "default",
};

const Others = () => {
  const { getSuggestions } = useUser();
  const [category, setCategory] = useState("");
  const { data: suggestions = [], isLoading, isError } = useQuery({
    queryKey: ["suggestions"],
    queryFn: getSuggestions,
  });

  const categories = useMemo(() => {
    const counts = {};
    suggestions.forEach((s) => {
      counts[s.category] = (counts[s.category] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [suggestions]);

  const shown = category ? suggestions.filter((s) => s.category === category) : suggestions;

  return (
    <div style={{ maxWidth: 820 }}>
      <AdminPageHeader
        title="Suggestions"
        subtitle={isLoading ? "Loading…" : `${suggestions.length} ideas from the community`}
      />

      {categories.length > 1 ? (
        <Radio.Group
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          optionType="button"
          style={{ marginBottom: 16, display: "flex", flexWrap: "wrap" }}
          options={[
            { label: `All (${suggestions.length})`, value: "" },
            ...categories.map(([name, n]) => ({ label: `${name} (${n})`, value: name })),
          ]}
        />
      ) : null}

      {isLoading ? (
        <div className="adm-card" style={{ padding: 20 }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      ) : isError ? (
        <div className="adm-card adm-empty">Could not load suggestions.</div>
      ) : shown.length === 0 ? (
        <div className="adm-card adm-empty">No suggestions yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {shown.map((s) => (
            <div key={s._id} className="adm-card" style={{ padding: "16px 20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <UserOutlined style={{ color: "var(--adm-muted)" }} />
                  <span style={{ fontWeight: 700 }}>{s.name || "Anonymous"}</span>
                  <Tag color={CATEGORY_COLORS[s.category] || "default"}>{s.category}</Tag>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--adm-muted)", fontSize: 12 }}>
                  <ClockCircleOutlined />
                  {new Date(s.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
              <p style={{ margin: 0, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{s.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Others;
